/**
 * GraphEvaluator — traverses the blueprint node graph and produces, per frame:
 *   - `plays`: one play request per Play Clip node (its own player — see
 *     "Play requests" below), which applyWeights drives;
 *   - `weights`: the per-clip totals { clipName → weight }, for readers that
 *     only need totals, plus the sentinel channels (__livelink__, …).
 *
 * Called every frame from AvatarModel.useFrame() using graphState.
 */

import { smRuntime, triggerTransition, pickClipForState } from './smRuntime'
import { resolveTransitionDuration } from './transitionDuration.js'
import { runTransitionActions } from './smTransitionActions'
import { enterState, exitState } from './smStateEvents'
import { normalizeOptions, pickIndex } from './probabilitySelect'
import { evaluateRule } from './RuleEvaluator'
import { debugState } from './debugState'
import { variableValues, getVariable } from './animVariables'
import { applyGesture, silenceGestures } from './GestureChannel'
import { getPluginNode as pluginRegistryGet } from './pluginNodeRegistry'
import { devLog } from './devLog'
import { headFollowRuntime } from './headFollowRuntime'
import { eyeFollowRuntime } from './eyeFollowRuntime.js'
import { lookRuntime } from './lookRuntime.js'
import { AdditiveAnimationBlendMode } from 'three'
import { setConfig as setBlinkConfig } from './blinkRuntime'
import { layeredBoneBlendRuntime } from './layeredBoneBlendRuntime'
import { getBodyClipLoader } from './bodyClipLoaderRef'
import { playerPoolFor, playerTiming } from './animPlayerPool.js'
import { rigControlState } from './rigControlState'
import { FACE_TRACK_SENTINEL } from './faceTrackState'
import { STREAM_FIRST_FRAME_CLIP, STREAM_LAST_FRAME_CLIP } from './sentenceFrameClips.js'
import { numberOr } from './fieldNumber.js'

const _missingClipWarned = new Set()

// Change-tracked per-SM clip log — only fires a console.log when the active
// clip (or active state) changes, never every frame.
// Key: `${smNodeId}:${stateId}`  Value: last-reported clip string
const _smLastClip = new Map()

// Per-frame value cache — cleared each evaluateGraph call, prevents duplicate eval within one frame
const _valueCache = new Map()

// Per-frame plugin-tick guard: nodeId -> the weight tick() ran with this frame.
// Guarantees a plugin node's tick() + sentinel emission happen exactly once per
// frame even though it can be reached via the sideband, graph traversal, AND pin
// reads. Cleared each evaluateGraph call alongside _valueCache. (See pluginTickOnce.)
const _pluginTicked = new Map()

// Sticky random values — persist across frames so Select doesn't thrash every tick.
// Each entry: { value, min, max, randomType } — re-rolls if config changes.
// Cleared per-node on SM state entry so each state entry picks fresh.
const _stickyValues = new Map()

// BlendByBool crossfade state — persists across frames to drive the interpolation.
// Each entry: { weight, target, startWeight, startTime, endTime }
//   weight     — current interpolated blend weight (0 = A, 1 = B)
//   target     — 0 or 1, the value we're blending toward
//   startWeight — weight at the moment the transition started
//   startTime  — performance.now() when the transition started
//   endTime    — performance.now() when the transition should be complete
const _blendByBoolState = new Map()

// PrefabSlot blend state — same shape as _blendByBoolState plus lastClipName so
// the blend-out phase can still reference the clip after __PREFAB_ACTIVE__ clears.
const _prefabSlotState = new Map()


// ── Play requests: one player per Play Clip node ────────────────────────────
//
// The `{ clipName → weight }` map sums every node that names a clip into one
// number, so two state machines on `idle` used to share a single action: one
// playhead, one speed, one loop mode (the first node's). Alongside that map the
// graph now emits a PLAY REQUEST per node — `{ clipName, weight, loop, speed,
// additive }` — and applyWeights gives each request its own player
// (animPlayerPool.js). The map stays as the per-clip total, for readers that
// only want totals (weight bars, the splat rig, the face clip player).
//
// A request is identified by (scope, nodeId). The root scope belongs to the
// graph; a state machine gives each ENTRY of a state a fresh scope, so
// re-entering a state — including a self-transition, whose outgoing and
// incoming copies are the same nodes — starts new players while the outgoing
// ones fade, instead of rewinding the copy that is still fading out.
//
// Requests are long-lived objects kept on their scope, re-stamped each frame:
// the hot path allocates nothing once a node has been seen. A scope is made
// once per state entry, never per frame.
let _evalFrame = 0
// A legacy state carries its clip on the state node itself, with no Play Clip
// node to key by; within the state's own scope this key is unique.
const LEGACY_STATE_CLIP_KEY = '__state_clip__'
const _rootScopes = new WeakMap()  // graph object → scope
const _smScopes   = new WeakMap()  // smRuntime entry → { curId, curAt, cur, prevId, prev }

function newScope(label, root = false) {
  return { label, root, requests: new Map() }
}

/** The scope for a Layered Bone Blend layer under `scope` — made once, kept on it. */
function layerScopeOf(scope) {
  if (!scope.layer) scope.layer = newScope(`${scope.label}/layer`)
  return scope.layer
}

function rootScopeFor(graph) {
  let s = _rootScopes.get(graph)
  if (!s) { s = newScope('root', true); _rootScopes.set(graph, s) }
  return s
}

/**
 * The scope for a state of a state machine. `incoming` is true for the current
 * state, false for the state a crossfade is leaving. A changed currentState or
 * stateEnteredAt is a new entry, so the old entry's scope becomes the outgoing
 * one and the new entry gets its own.
 */
function stateScopeFor(runtime, smId, stateId, incoming) {
  let s = _smScopes.get(runtime)
  if (!s) {
    s = { curId: null, curAt: null, cur: null, prevId: null, prev: null }
    _smScopes.set(runtime, s)
  }
  if (runtime.currentState !== s.curId || runtime.stateEnteredAt !== s.curAt) {
    s.prevId = s.curId
    s.prev   = s.cur
    s.curId  = runtime.currentState
    s.curAt  = runtime.stateEnteredAt
    s.cur    = newScope(`${smId}/${s.curId}`)
  }
  if (incoming) return s.cur
  if (s.prevId !== stateId || !s.prev) {
    s.prevId = stateId
    s.prev   = newScope(`${smId}/${stateId}`)
  }
  return s.prev
}

/**
 * Add `weight` to the request for (scope, key), pushing it onto `plays` on the
 * first touch of a frame. The first touch resets it and records its settings;
 * later touches in the same frame (a node reached through two edges) only add
 * weight. Returns null where requests are not collected (a Layered Bone
 * Blend's layer, which its runtime drives).
 */
function touchRequest(scope, plays, key, clipName, weight, loop, additive) {
  if (!scope || !plays) return null
  let r = scope.requests.get(key)
  if (!r) {
    r = {
      scope, nodeId: key, clipName, weight: 0, loop, speed: 1, additive,
      frame: -1, touches: 0,
      // A Layered Bone Blend layer clip: played silently, weighted by its runtime.
      layer: plays === _layerPlays,
      // Owned by applyWeights:
      player: null, appliedFrame: -1, live: false,
    }
    scope.requests.set(key, r)
  }
  if (r.frame !== _evalFrame) {
    r.frame    = _evalFrame
    r.weight   = 0
    r.clipName = clipName
    r.loop     = loop
    r.additive = additive
    r.speed    = 1
    r.touches  = 0
    plays.push(r)
  }
  r.touches++
  r.weight += weight
  return r
}

// Scratch for the heaviest-request scan below (Map.forEach with a module-level
// callback: no iterator or closure per frame).
let _heaviest = null
let _heaviestWeight = 0
function _pickHeaviest(r) {
  let w = 0
  if (r.live && r.player) w = r.player.action.getEffectiveWeight()
  // A face clip's playhead (FaceClipPlayer): its weight as last blended, if the
  // request was reached by the last face-graph evaluation (body and face each
  // evaluate once a frame, so two counts back covers it).
  else if (r._face && r.frame >= _evalFrame - 2) w = r._face.weight
  if (w > _heaviestWeight) { _heaviestWeight = w; _heaviest = r }
}

/** A request's timing into `out`: its mixer player, else its face playhead. */
function requestTiming(r, out) {
  if (!r) return false
  if (r.player) return playerTiming(r.player.action, out)
  const st = r._face
  const duration = st?.duration
  if (!(duration > 0)) return false
  const time = st.loop ? st.local % duration : Math.min(st.local, duration)
  out.time      = time
  out.duration  = duration
  out.remaining = duration - time
  out.ratio     = time / duration
  return true
}

/**
 * The timing of the current state's own player, for the anim-time rules,
 * written into debugState.smClipTimings[smId] (one object per state machine,
 * reused) and returned.
 *
 * The player is the Play Clip wired to the state's output when there is one,
 * else the heaviest player of this state ENTRY — which is the clip a Select /
 * Random inside the state picked. (By clip name, a state whose output is a
 * Select had no clip at all and its rules read "999 s remaining" forever.) A
 * face clip's "player" is its playhead in FaceClipPlayer, kept on the same
 * request.
 *
 * Null when the state has no player of its own yet (not evaluated since it was
 * entered, or a clip that is not loaded). The rule then falls back to the
 * per-clip timing.
 */
function stateClipTiming(runtime, smId, playNode) {
  let out = debugState.smClipTimings[smId]
  if (!out) out = debugState.smClipTimings[smId] = { stateId: null, time: 0, duration: 0, remaining: 0, ratio: 0 }
  out.stateId = null
  const s = _smScopes.get(runtime)
  // The scope must be THIS entry's: until the state is first evaluated after
  // entering, s.cur still holds the previous entry's players.
  if (!s || !s.cur || s.curId !== runtime.currentState || s.curAt !== runtime.stateEnteredAt) return null
  let r
  if (playNode) {
    r = s.cur.requests.get(playNode.id)
  } else {
    _heaviest = null
    _heaviestWeight = 0
    s.cur.requests.forEach(_pickHeaviest)
    r = _heaviest
    _heaviest = null
  }
  if (!requestTiming(r, out)) return null
  out.stateId = runtime.currentState
  return out
}

/** A Play Clip node's speed: a wired positive `speed-in` wins over the typed Speed. */
function playClipSpeed(node, nodeId, ctx) {
  const typed = Number(node.data.playSpeed)
  let speed = Number.isFinite(typed) && typed > 0 ? typed : 1.0
  // A wired `speed-in` (a Get Variable, a Random, …) wins over the typed
  // Speed, re-read every frame. A Random inside a state re-rolls on each
  // entry (clearStickyRandomsInNodes), which is what makes per-entry
  // randomised speed work. A wired value that is not a positive number
  // keeps the typed Speed, the same rule the typed field has always had.
  const speedEdge = findEdgeByHandle(ctx, nodeId, 'speed-in')
  if (speedEdge) {
    const wired = evaluateValue(speedEdge.source, ctx)
    if (Number.isFinite(wired) && wired > 0) speed = wired
  }
  return speed
}

export function clearStickyValues(nodeIds) {
  for (const id of nodeIds) _stickyValues.delete(id)
}

/**
 * Delete sticky values for every 'random' node in `nodes`, without
 * allocating an intermediate filter/map array. Called per-frame from
 * AvatarModel when a non-looping clip ends, so keep it tight.
 */
export function clearStickyRandomsInNodes(nodes) {
  if (!nodes) return
  for (const n of nodes) {
    if (n.type === 'random' || n.type === 'probabilitySelect') _stickyValues.delete(n.id)
  }
}

function _rollRandom(min, max, randomType) {
  return randomType === 'int'
    ? Math.floor(Math.random() * (Math.floor(max) - Math.ceil(min) + 1)) + Math.ceil(min)
    : Math.random() * (max - min) + min
}

/**
 * Build an evaluation context with O(1) lookups. Called once per evaluateGraph
 * and once per state's inner graph (state machines have nested graphs that need
 * their own index). The result/loopMap/additiveMap accumulators are shared
 * across parent + child contexts so inner-state evaluation contributes to the
 * same output.
 */
function buildCtx(nodes, edges, result, loopMap, additiveMap, speedMap) {
  const nodeById = new Map()
  for (const n of nodes) nodeById.set(n.id, n)
  const edgesByTarget = new Map()
  for (const e of edges) {
    let bucket = edgesByTarget.get(e.target)
    if (!bucket) { bucket = []; edgesByTarget.set(e.target, bucket) }
    bucket.push(e)
  }
  return { nodeById, edgesByTarget, result, loopMap, additiveMap, speedMap }
}

function findEdgeByHandle(ctx, targetId, handle) {
  const bucket = ctx.edgesByTarget.get(targetId)
  if (!bucket) return undefined
  for (const e of bucket) if (e.targetHandle === handle) return e
  return undefined
}

/**
 * Evaluate a value-producing node (e.g. Random), returning a number.
 * Random nodes are sticky: once rolled they hold their value across frames.
 * Re-rolls when clearStickyValues() is called OR when min/max/type changes.
 */
function evaluateValue(nodeId, ctx) {
  if (_valueCache.has(nodeId)) return _valueCache.get(nodeId)
  const node = ctx.nodeById.get(nodeId)
  let value = 0
  // Plugin scalar source: pull the node's primary scalar output via readPin. The
  // producer is ticked once (guarded) so its value is fresh before we read it.
  // evaluateValue is keyed by node (not pin), so this resolves the FIRST dataOut
  // pin — a value source has one logical scalar output, like Random/getVariable.
  if (node?.type?.startsWith('plugin.')) {
    const entry = pluginRegistryGet(node.type)
    pluginTickOnce(node, entry, ctx)
    const outPins = entry?.nodeManifest?.pins?.dataOut
    const pinId = (outPins && outPins.length) ? outPins[0].id : 'out'
    const raw = entry?.instance?.readPin?.(pinId, node.data, node.id)
    value = (typeof raw === 'boolean') ? (raw ? 1 : 0)
          : (typeof raw === 'number' && Number.isFinite(raw)) ? raw : 0
    _valueCache.set(nodeId, value)
    return value
  }
  if (node?.type === 'getVariable') {
    value = Number(getVariable(node.data.variableName)) || 0
    _valueCache.set(nodeId, value)
    return value
  }
  if (node?.type === 'prefabSlot') {
    // If evaluateNode already ran this slot, use the cached is-active value.
    // Otherwise compute it directly (e.g. when only the alpha pin is wired).
    if (_valueCache.has(nodeId)) return _valueCache.get(nodeId)
    const state = typeof window !== 'undefined' ? window.__PREFAB_ACTIVE__ : null
    value = (state?.clipName && Date.now() < state.expiresAt) ? 1 : 0
    _valueCache.set(nodeId, value)
    return value
  }
  if (node?.type === 'random') {
    const min        = node.data.min        ?? 0
    const max        = numberOr(node.data.max, 1)
    const randomType = node.data.randomType ?? 'float'
    const sticky     = _stickyValues.get(nodeId)
    if (sticky && sticky.min === min && sticky.max === max && sticky.randomType === randomType) {
      value = sticky.value
    } else {
      value = _rollRandom(min, max, randomType)
      _stickyValues.set(nodeId, { value, min, max, randomType })
    }
  }
  _valueCache.set(nodeId, value)
  return value
}

/**
 * Tick a plugin node at most once per frame, returning the weight tick() ran
 * with. The _pluginTicked guard is set BEFORE invoking tick() so a re-entrant
 * pin read during tick() can't recurse into the same node. masterWeight === null
 * means "use the node's own weight" (sideband / pin-read paths); a number scales
 * it (the traversal path). Errors are swallowed — one bad plugin never stalls the
 * 60Hz loop.
 */
function pluginTickOnce(node, entry, ctx, masterWeight = null) {
  const prev = _pluginTicked.get(node.id)
  if (prev !== undefined) return prev
  const enabled = node.data?.enabled ?? true
  const base = masterWeight != null ? masterWeight : 1.0
  const w = enabled ? base * (node.data?.weight ?? 1.0) : 0
  _pluginTicked.set(node.id, w)
  if (entry?.instance?.tick) {
    try { entry.instance.tick(w, node.data, node.id, pinReaderFor(ctx)) }
    catch (e) { console.error(`[GraphEvaluator] plugin tick failed for ${node.type}:`, e) }
  }
  return w
}

/**
 * Per-ctx, per-frame typed-pin reader handed to plugin tick() as its 4th arg.
 * Lets a plugin pull values wired into its OWN input pins, reusing the existing
 * edge index + evaluateValue (allocation-free, memoized). Built once per graph
 * level per frame (memoized on ctx), not per node.
 */
function pinReaderFor(ctx) {
  if (ctx._pinReader) return ctx._pinReader
  const reader = Object.freeze({
    // Is nodeId's input `pinId` wired to an upstream output?
    isWired(nodeId, pinId) {
      return findEdgeByHandle(ctx, nodeId, pinId) !== undefined
    },
    // Read a scalar (number, or boolean→0|1) for nodeId's input `pinId`: the wired
    // upstream value when connected, else the node's own inline value (data[pinId],
    // set by the node's number/checkbox field), else 0.
    readScalarIn(nodeId, pinId) {
      const edge = findEdgeByHandle(ctx, nodeId, pinId)
      if (edge) return evaluateValue(edge.source, ctx)
      const inline = ctx.nodeById.get(nodeId)?.data?.[pinId]
      if (typeof inline === 'number') return Number.isFinite(inline) ? inline : 0
      if (typeof inline === 'boolean') return inline ? 1 : 0
      return 0
    },
    // Read the buffer/anim value of the producer wired into nodeId's input
    // `pinId`: resolve the edge, ensure the producer ticked, return its readPin
    // view (a retained typed array — do NOT mutate or retain). null when unwired.
    readBufferIn(nodeId, pinId) {
      const edge = findEdgeByHandle(ctx, nodeId, pinId)
      if (!edge) return null
      const src = ctx.nodeById.get(edge.source)
      if (!src) return null
      const e = pluginRegistryGet(src.type)
      pluginTickOnce(src, e, ctx)
      return e?.instance?.readPin?.(edge.sourceHandle, src.data, src.id) ?? null
    },
  })
  ctx._pinReader = reader
  return reader
}

/**
 * Recursively evaluate a node, returning the weighted contribution
 * of each animation clip.
 *
 * @param {string} nodeId
 * @param {{nodeById: Map, edgesByTarget: Map, result: Map, loopMap: Map, additiveMap: Map}} ctx
 * @param {number}  masterWeight — weight passed down from parent
 */
function evaluateNode(nodeId, ctx, masterWeight, isAdditive = false) {
  const node = ctx.nodeById.get(nodeId)
  if (!node) return

  if (node.type === 'playClip') {
    const clipName = node.data.clipName
    if (clipName) {
      // Per-node loop (ENG-524). Default FALSE — a clip plays once and holds
      // its final frame unless Loop is explicitly enabled. Existing blueprint
      // nodes (incl. the shipped idles/breathing/listening) carry an explicit
      // `loop: true`, so they keep looping; only nodes without the field (or
      // set to false) are one-shot. applyWeights turns false into LoopOnce +
      // clampWhenFinished (hold final frame), exactly like Unreal's non-loop clamp.
      const loop = node.data.loop === true
      // This node's own player. Loop, speed and additive are the NODE's, so a
      // second node on the same clip keeps its own settings.
      const r = touchRequest(ctx.scope, ctx.plays, nodeId, clipName, masterWeight, loop, isAdditive)
      const fresh = r !== null && r.touches === 1
      const speed = (fresh || (ctx.speedMap && !ctx.speedMap.has(clipName)))
        ? playClipSpeed(node, nodeId, ctx) : 1
      if (fresh) r.speed = speed

      // The per-clip totals. Several nodes on one clip sum here, and the
      // settings are the first node's — which is why players no longer read it.
      ctx.result.set(clipName, (ctx.result.get(clipName) ?? 0) + masterWeight)
      if (!ctx.loopMap.has(clipName)) ctx.loopMap.set(clipName, loop)
      if (!ctx.additiveMap.has(clipName)) ctx.additiveMap.set(clipName, isAdditive)
      if (ctx.speedMap && !ctx.speedMap.has(clipName)) ctx.speedMap.set(clipName, speed)
    }
    return
  }

  // Prefab slot — activates when window.__PREFAB_ACTIVE__ is set.
  //
  // Two modes, chosen by whether a `pose` input edge is wired:
  //
  // Merged mode (pose wired): self-contained crossfade between the incoming pose
  //   and the prefab clip — no external BlendByBool needed.
  //
  // Legacy mode (pose not wired): outputs the prefab clip weight unchanged so
  //   an external BlendByBool can drive the crossfade (old wiring still works).
  //
  // Either way, is-active (0/1) is cached in _valueCache for downstream pins.
  if (node.type === 'prefabSlot') {
    const prefabGlobal = typeof window !== 'undefined' ? window.__PREFAB_ACTIVE__ : null
    const active       = !!(prefabGlobal?.clipName && Date.now() < prefabGlobal.expiresAt)
    const target       = active ? 1 : 0

    _valueCache.set(node.id, target)

    const poseEdge = findEdgeByHandle(ctx, nodeId, 'pose')

    if (poseEdge) {
      // ── Merged mode ──────────────────────────────────────────────────────
      let bs = _prefabSlotState.get(nodeId)
      if (!bs) {
        bs = { weight: target, target, startWeight: target, startTime: 0, endTime: 0, lastClipName: null }
        _prefabSlotState.set(nodeId, bs)
      }

      if (active) bs.lastClipName = prefabGlobal.clipName

      if (target !== bs.target) {
        const blendMs = (target === 1
          ? (node.data.blendInTime  ?? 0.1)
          : (node.data.blendOutTime ?? 0.1)) * 1000
        const now = performance.now()
        bs.startWeight = bs.weight
        bs.startTime   = now
        bs.endTime     = now + blendMs
        bs.target      = target
      }

      const now = performance.now()
      if (now >= bs.endTime) {
        bs.weight = bs.target
      } else {
        const t = (now - bs.startTime) / (bs.endTime - bs.startTime)
        bs.weight = bs.startWeight + (bs.target - bs.startWeight) * Math.max(0, Math.min(1, t))
      }

      const w = bs.weight  // 0 = full input pose, 1 = full prefab clip

      if (w < 1) evaluateNode(poseEdge.source, ctx, masterWeight * (1 - w), isAdditive)

      const clipBase = bs.lastClipName
      if (clipBase && w > 0) {
        const isFace   = node.data?._graphType === 'face'
        const clipName = isFace ? `${clipBase}_face` : clipBase
        touchRequest(ctx.scope, ctx.plays, nodeId, clipName, masterWeight * w, false, isAdditive)
        ctx.result.set(clipName, (ctx.result.get(clipName) ?? 0) + masterWeight * w)
        ctx.loopMap.set(clipName, false)
        ctx.additiveMap.set(clipName, isAdditive)
      }

      if (w === 0) bs.lastClipName = null

    } else {
      // ── Legacy mode — pose pin not wired, external BlendByBool drives blend ──
      if (active) {
        const isFace   = node.data?._graphType === 'face'
        const clipName = isFace ? `${prefabGlobal.clipName}_face` : prefabGlobal.clipName
        touchRequest(ctx.scope, ctx.plays, nodeId, clipName, masterWeight, false, isAdditive)
        ctx.result.set(clipName, (ctx.result.get(clipName) ?? 0) + masterWeight)
        ctx.loopMap.set(clipName, false)
        ctx.additiveMap.set(clipName, isAdditive)
      }
    }

    return
  }

  if (node.type === 'liveLink') {
    // Sentinel clip name — HeadModel watches for this to drive ARKit blendshapes
    ctx.result.set('__livelink__', (ctx.result.get('__livelink__') ?? 0) + masterWeight)
    return
  }

  // First/Last Frame Pose — source nodes that emit the static face pose the
  // Stream First/Last Frame event actions published (a 1-frame clip in
  // faceClipPlayer). Ordinary clip-weight sources: an upstream Blend / Blend by
  // Bool scales them like any other clip, and both the GLB head and the splat
  // rig resolve them via getBlendedWeights when LiveLink is off. The clip is
  // absent until the event action fires, in which case getBlendedWeights simply
  // skips it (rest) — never an error.
  if (node.type === 'firstFramePose') {
    ctx.result.set(STREAM_FIRST_FRAME_CLIP, (ctx.result.get(STREAM_FIRST_FRAME_CLIP) ?? 0) + masterWeight)
    return
  }
  if (node.type === 'lastFramePose') {
    ctx.result.set(STREAM_LAST_FRAME_CLIP, (ctx.result.get(STREAM_LAST_FRAME_CLIP) ?? 0) + masterWeight)
    return
  }

  if (node.type === 'rigController') {
    // Sentinel clip name — HeadModel (GLB morphs), OGSModel (splat rig) and
    // StreamAvatarModel (stream rig) watch for this to drive the face from
    // rigControlState (the Rig Preview sliders) instead of LiveLink/clips.
    // The graph owns the routing: this is an ordinary pose source, so an
    // upstream Blend scales it like any other.
    ctx.result.set('__rigcontrol__', (ctx.result.get('__rigcontrol__') ?? 0) + masterWeight)
    // Pose vs passthrough is authored on the NODE, so the values module never
    // has to know which blueprint is loaded.
    rigControlState.passthrough = node.data?.passthrough === true
    return
  }

  if (node.type === 'faceTracker') {
    // Sentinel weight (ENG-1107) — every face renderer resolves it through
    // faceTrackWeight(): the webcam frame at this weight while the camera is
    // tracking, nothing at all while it is not (so a plugged node with the
    // camera blocked falls through to LiveLink/clips instead of freezing the
    // face). Arming is NOT done here: faceTrackState's watcher reads the graph
    // at UI cadence and starts/stops the camera on plug/unplug.
    ctx.result.set(FACE_TRACK_SENTINEL, (ctx.result.get(FACE_TRACK_SENTINEL) ?? 0) + masterWeight)
    return
  }


  // A Look node only acts through Final Pose's `look` input (evaluateLook);
  // reached any other way it contributes nothing.
  if (node.type === 'look') return

  if (node.type === 'headFollow') {
    // Sentinel weight — AvatarModel picks this up post-mixer to rotate the
    // neck chain toward the camera. Per-node config (max angles, smoothing)
    // is stashed on the runtime singleton; only one active headFollow per
    // face graph is meaningful (last-writer-wins).
    ctx.result.set('__headfollow__', (ctx.result.get('__headfollow__') ?? 0) + masterWeight)
    headFollowRuntime.setConfig(node.data)
    return
  }

  if (node.type === 'eyeFollow') {
    // Sentinel weight — the renderer that owns the avatar picks this up
    // post-mixer and spends it three ways: ARKit EyeLook* morphs (GLB), the
    // splat rig's own eye controls, and a `neckWeight` share added to
    // __headfollow__ so the neck turn reuses the head-follow chain rather than
    // getting a second implementation. Same last-writer-wins config rule as
    // headFollow — only one active eyeFollow per face graph is meaningful.
    // Every field is pin-wireable (`in-<field>`), like blink.
    ctx.result.set('__eyefollow__', (ctx.result.get('__eyefollow__') ?? 0) + masterWeight)
    const resolved = {}
    for (const field of ['eyeWeight', 'neckWeight', 'maxYaw', 'maxPitch', 'yawRange', 'pitchRange', 'smoothing', 'deadbandDeg']) {
      const edge = findEdgeByHandle(ctx, nodeId, `in-${field}`)
      resolved[field] = edge ? evaluateValue(edge.source, ctx) : node.data[field]
    }
    eyeFollowRuntime.setConfig(resolved)
    return
  }

  if (node.type === 'blink') {
    // Sentinel weight — HeadModel applies procedural blinks to morph targets
    // at the weight emitted here. Config (interval, durations) is stashed on
    // the blink runtime singleton, same pattern as headFollow.
    // Each parameter can be wired from another node (Random, GetVariable, etc.)
    // via an `in-<field>` handle, falling back to the node's own data value.
    ctx.result.set('__blink__', (ctx.result.get('__blink__') ?? 0) + masterWeight)
    const resolved = {}
    for (const field of ['minInterval', 'maxInterval', 'closeMs', 'holdMs', 'openMs']) {
      const edge = findEdgeByHandle(ctx, nodeId, `in-${field}`)
      resolved[field] = edge ? evaluateValue(edge.source, ctx) : node.data[field]
    }
    setBlinkConfig(resolved)
    return
  }

  if (node.type === 'blend') {
    let alpha = node.data.alpha ?? 0.5
    // Wired value input takes priority, then variable, then slider
    const alphaEdge = findEdgeByHandle(ctx, nodeId, 'alpha-in')
    if (alphaEdge) {
      alpha = evaluateValue(alphaEdge.source, ctx)
    } else if (node.data.alphaVariable) {
      const varVal = variableValues[node.data.alphaVariable]
      if (varVal !== undefined && varVal !== null) alpha = Number(varVal) || 0
    }
    const isAdditive = node.data.blendMode === 'additive'
    const aEdge = findEdgeByHandle(ctx, nodeId, 'a')
    const bEdge = findEdgeByHandle(ctx, nodeId, 'b')

    // Normal blend: A and B share the weight budget (1-alpha, alpha).
    // Additive blend: A is the base pose at full weight; B is a layer on
    // top, scaled by alpha. This lets sentinel channels (like __livelink__
    // in A) pass through unmuted while B (e.g. __headfollow__) stacks on.
    if (isAdditive) {
      if (aEdge) evaluateNode(aEdge.source, ctx, masterWeight,         false)
      if (bEdge) evaluateNode(bEdge.source, ctx, masterWeight * alpha, true)
    } else {
      if (aEdge) evaluateNode(aEdge.source, ctx, masterWeight * (1 - alpha), false)
      if (bEdge) evaluateNode(bEdge.source, ctx, masterWeight * alpha,       false)
    }
    return
  }

  if (node.type === 'blendByBool') {
    // Resolve bool input (wired > variable > toggle)
    let val = node.data.defaultValue ? 1 : 0
    const boolEdge = findEdgeByHandle(ctx, nodeId, 'bool-in')
    if (boolEdge) {
      val = evaluateValue(boolEdge.source, ctx)
    } else if (node.data.boolVariable) {
      const v = variableValues[node.data.boolVariable]
      if (v !== undefined && v !== null) val = Number(v) || 0
    }
    const wantTrue = val > 0.5
    const target   = wantTrue ? 1 : 0

    // Retrieve or initialise per-node blend state
    let state = _blendByBoolState.get(nodeId)
    if (!state) {
      // First frame — snap to target with no blend
      state = { weight: target, target, startWeight: target, startTime: 0, endTime: 0 }
      _blendByBoolState.set(nodeId, state)
    }

    // Detect a target flip → start a new crossfade
    if (target !== state.target) {
      // Blend time: wired pin (e.g. Get Variable) > typed value > 0.1 s.
      // Read only on a flip, so a wired value costs nothing per frame.
      const key = wantTrue ? 'trueBlendTime' : 'falseBlendTime'
      const timeEdge = findEdgeByHandle(ctx, nodeId, `${key}-in`)
      const seconds = timeEdge
        ? numberOr(evaluateValue(timeEdge.source, ctx), 0.1)
        : numberOr(node.data[key], 0.1)
      const blendMs = Math.max(0, seconds) * 1000
      const now = performance.now()
      state.startWeight = state.weight
      state.startTime   = now
      state.endTime     = now + blendMs
      state.target      = target
    }

    // Advance the interpolation
    const now = performance.now()
    if (now >= state.endTime) {
      state.weight = state.target
    } else {
      const t = (now - state.startTime) / (state.endTime - state.startTime)
      state.weight = state.startWeight + (state.target - state.startWeight) * Math.max(0, Math.min(1, t))
    }

    const w      = state.weight  // 0 = full A, 1 = full B
    const aEdge  = findEdgeByHandle(ctx, nodeId, 'a')
    const bEdge  = findEdgeByHandle(ctx, nodeId, 'b')
    if (aEdge && w < 1) evaluateNode(aEdge.source, ctx, masterWeight * (1 - w), isAdditive)
    if (bEdge && w > 0) evaluateNode(bEdge.source, ctx, masterWeight * w,       isAdditive)
    return
  }

  if (node.type === 'select') {
    // Resolve index: wired > data.index
    const idxEdge = findEdgeByHandle(ctx, nodeId, 'in-index')
    let index = node.data.index ?? 0
    if (idxEdge) index = Math.round(evaluateValue(idxEdge.source, ctx))
    const optionCount = node.data.optionCount ?? 2
    const clamped     = Math.max(0, Math.min(optionCount - 1, index))
    const optionEdge  = findEdgeByHandle(ctx, nodeId, `option-${clamped}`)
    if (optionEdge) evaluateNode(optionEdge.source, ctx, masterWeight, isAdditive)
    return
  }

  // Probability Select: one input, drawn by weight, and kept — the same sticky
  // store and re-roll as Random (re-drawn when its state is entered again,
  // clearStickyRandomsInNodes). Editing the probabilities re-draws too: the
  // sticky entry remembers the options array it was drawn from, and the editor
  // replaces that array on every change. Draws only on those occasions, so no
  // per-frame allocation.
  if (node.type === 'probabilitySelect') {
    const options = node.data.options
    let sticky = _stickyValues.get(nodeId)
    if (!sticky || sticky.options !== options) {
      sticky = { value: pickIndex(normalizeOptions(options)), options }
      _stickyValues.set(nodeId, sticky)
    }
    const optionEdge = findEdgeByHandle(ctx, nodeId, `option-${sticky.value}`)
    if (optionEdge) evaluateNode(optionEdge.source, ctx, masterWeight, isAdditive)
    return
  }

  if (node.type === 'layeredBoneBlend') {
    const weightEdge = findEdgeByHandle(ctx, nodeId, 'weight-in')
    let blendWeight = node.data.blendWeight ?? 1.0
    if (weightEdge) blendWeight = evaluateValue(weightEdge.source, ctx)
    blendWeight = Math.max(0, Math.min(1, blendWeight))

    const baseEdge  = findEdgeByHandle(ctx, nodeId, 'base')
    const layerEdge = findEdgeByHandle(ctx, nodeId, 'layer')

    // Base input → normal result (full-body clips, drive pass 1)
    if (baseEdge) evaluateNode(baseEdge.source, ctx, masterWeight, isAdditive)

    // Layer input → separate result map (drives pass 2 subtree-only blend).
    // Evaluated at the node's own weight in the graph (masterWeight — < 1 while
    // its state crossfades), NOT × Weight: the runtime applies Weight once, on
    // top of that, so the blend fades out with its state.
    if (layerEdge && ctx.lbbResult) {
      const lbbCtx = {
        nodeById:      ctx.nodeById,
        edgesByTarget: ctx.edgesByTarget,
        result:        ctx.lbbResult,
        loopMap:       ctx.lbbLoopMap,
        additiveMap:   ctx.lbbAdditiveMap,
        speedMap:      ctx.speedMap,
        // The layer's clips get their own players, like any Play Clip, but in
        // a separate list: applyWeights keeps them playing SILENTLY and the
        // layer runtime lends them weight for its own pass. A scope of their
        // own, under the parent's, so a node can't share a player across the
        // base and the layer.
        scope:         ctx.scope ? layerScopeOf(ctx.scope) : null,
        plays:         ctx.scope ? _layerPlays : null,
      }
      evaluateNode(layerEdge.source, lbbCtx, masterWeight, isAdditive)
    }

    // Reused config — no per-frame allocation. blendBones keeps the node's own
    // array (a new one only when edited), so the runtime can cache its branch
    // flags by reference.
    _lbbConfig.blendBones  = lbbBones(node.data)
    _lbbConfig.blendWeight = blendWeight
    _lbbConfig.mode        = node.data.blendMode === 'additive' ? 'additive' : 'override'
    _lbbConfig.additiveRef = node.data.additiveRef === 'rest' ? 'rest' : 'firstFrame'
    // Copied: _layerPlays is reset by the next evaluateGraph (the face graph,
    // later this frame), and the runtime reads this after applyWeights.
    _lbbConfig.layer.length = 0
    for (let i = 0; i < _layerPlays.length; i++) _lbbConfig.layer.push(_layerPlays[i])
    layeredBoneBlendRuntime.setConfig(_lbbConfig)
    return
  }

  if (node.type === 'stateMachine') {
    const runtime = smRuntime[nodeId]
    if (!runtime) return

    const innerNodes = node.data.innerNodes ?? []

    // Evaluate a state's inner graph (PlayClip/Blend → output) at given weight.
    // Falls back to the legacy state.data.clipName / clips[] format for
    // blueprints authored before StateOutputNode existed.
    // `incoming` — the current state (true) or the one a crossfade is leaving.
    const evalState = (stateId, weight, incoming) => {
      const stateNode = innerNodes.find(n => n.id === stateId)
      if (!stateNode) return
      const sNodes = stateNode.data?.innerNodes ?? []
      const sEdges = stateNode.data?.innerEdges ?? []
      // Players for this ENTRY of the state (see stateScopeFor). Only where
      // the parent collects requests: inside a Layered Bone Blend's layer it
      // does not.
      const scope = ctx.plays ? stateScopeFor(runtime, nodeId, stateId, incoming) : null
      let outputEdge
      for (const e of sEdges) { if (e.target === 'output') { outputEdge = e; break } }
      if (outputEdge) {
        // Build a sub-ctx with fresh index maps for this state's inner graph
        const subCtx = buildCtx(sNodes, sEdges, ctx.result, ctx.loopMap, ctx.additiveMap, ctx.speedMap)
        subCtx.scope = scope
        subCtx.plays = ctx.plays
        // A Layered Bone Blend inside the state writes its layer here.
        subCtx.lbbResult      = ctx.lbbResult
        subCtx.lbbLoopMap     = ctx.lbbLoopMap
        subCtx.lbbAdditiveMap = ctx.lbbAdditiveMap
        evaluateNode(outputEdge.source, subCtx, weight, isAdditive)
        // Log subgraph path once per state activation
        const sgKey = `${nodeId}:${stateId}`
        if (_smLastClip.get(sgKey) !== '(subgraph)') {
          _smLastClip.set(sgKey, '(subgraph)')
          devLog(`[SM] ${nodeId} | state "${stateId}" → (subgraph) wt=${weight.toFixed(2)}`)
        }
        return
      }
      const legacyClip = stateId === runtime.currentState && runtime.pickedClip
        ? runtime.pickedClip
        : stateId === runtime.transition?.from && runtime.transition?.fromPickedClip
          ? runtime.transition.fromPickedClip
          : pickClipForState(stateNode)
      if (legacyClip) {
        // Log once when the clip for this state changes (re-rolls, state re-entry, etc.)
        const lcKey = `${nodeId}:${stateId}`
        if (_smLastClip.get(lcKey) !== legacyClip) {
          _smLastClip.set(lcKey, legacyClip)
          devLog(`[SM] ${nodeId} | state "${stateId}" → clip "${legacyClip}" wt=${weight.toFixed(2)}`)
        }
        if (scope) touchRequest(scope, ctx.plays, LEGACY_STATE_CLIP_KEY, legacyClip, weight, true, isAdditive)
        ctx.result.set(legacyClip, (ctx.result.get(legacyClip) ?? 0) + weight)
        if (!ctx.loopMap.has(legacyClip)) ctx.loopMap.set(legacyClip, true)
        if (!ctx.additiveMap.has(legacyClip)) ctx.additiveMap.set(legacyClip, isAdditive)
      }
    }

    // During a crossfade transition between two states
    if (runtime.transition) {
      const elapsed  = (Date.now() - runtime.transition.startTime) / 1000
      const progress = Math.min(elapsed / runtime.transition.duration, 1)

      if (progress >= 1) {
        runtime.transition = null
      } else {
        evalState(runtime.transition.from, masterWeight * (1 - progress), false)
        evalState(runtime.transition.to,   masterWeight * progress,       true)
        return
      }
    }

    // Normal — evaluate current state's inner graph at full weight
    evalState(runtime.currentState, masterWeight, true)
    return
  }

  // Plugin nodes — fall-through dispatch for face/body nodes contributed by the
  // plugin system. tick() is fire-and-forget (plugins schedule their own async
  // work) so we never await on the hot path. pluginTickOnce guarantees exactly one
  // tick per node per frame across the sideband, traversal, and pin reads.
  if (node.type.startsWith('plugin.')) {
    const entry = pluginRegistryGet(node.type)
    // postMixer (deformer) nodes run after mixer.update via evaluatePostMixer,
    // never in the pre-mixer weight pass — skip if traversal reaches one.
    if (entry?.nodeManifest?.phase === 'postMixer') return
    const w = pluginTickOnce(node, entry, ctx, masterWeight)
    // A plugin may declare a sentinel clip emitted into the weight map so
    // downstream consumers watching the sentinel react. perFrame
    // nodes already emitted theirs in the sideband — emitting again here would
    // DOUBLE the weight (the bug this dedup fixes), so guard on !perFrame.
    const sentinel = entry?.nodeManifest?.sentinelClip
    if (sentinel && w > 0 && !entry?.nodeManifest?.perFrame) {
      ctx.result.set(sentinel, (ctx.result.get(sentinel) ?? 0) + w)
    }
    return
  }
}

/**
 * Run a Look node (wired into Final Pose's `look` input) for this frame and
 * emit its head / eye / blink sentinel weights. Its `active` and
 * `holdEyeContact` inputs (or any setting) can be wired from a value source.
 */
// Reused reader (no per-frame closure): what's wired into the Look node's inputs.
let _lookCtx = null
let _lookId = null
function _lookReadWired(field) {
  const edge = findEdgeByHandle(_lookCtx, _lookId, field)
  return edge ? evaluateValue(edge.source, _lookCtx) : undefined
}

function evaluateLook(nodeId, ctx) {
  const node = ctx.nodeById.get(nodeId)
  if (!node || node.type !== 'look') return
  const enabled = node.data?.enabled ?? true
  const weight = enabled ? (node.data?.weight ?? 1) : 0
  _lookCtx = ctx
  _lookId = nodeId
  const w = lookRuntime.step(node.data, _lookReadWired, weight)
  _lookCtx = null
  if (w.head > 0) ctx.result.set('__headfollow__', (ctx.result.get('__headfollow__') ?? 0) + w.head)
  if (w.eye > 0) ctx.result.set('__eyefollow__', (ctx.result.get('__eyefollow__') ?? 0) + w.eye)
  if (w.blink > 0) ctx.result.set('__blink__', (ctx.result.get('__blink__') ?? 0) + w.blink)
}

/**
 * Output pool keyed by graphState.nodes identity. evaluateGraph is on the
 * 60 Hz hot path; allocating fresh `{weights, loopSettings, additiveSettings}`
 * + 3× Object.fromEntries every frame for both body and face was the
 * dominant source of GC churn after the gesture / per-clip-GLB landings.
 * The pool keeps one output object per graph and mutates its inner maps
 * in place each frame.
 */
const _outputPool = new WeakMap()
const EMPTY_OUTPUT = { weights: null, loopSettings: {}, additiveSettings: {}, speedSettings: {}, plays: [], layerPlays: [] }

function _getOutput(nodes) {
  let out = _outputPool.get(nodes)
  if (!out) {
    out = { weights: {}, loopSettings: {}, additiveSettings: {}, speedSettings: {}, plays: [], layerPlays: [] }
    _outputPool.set(nodes, out)
  }
  if (!out.speedSettings) out.speedSettings = {}
  return out
}

// Collects play requests while a graph evaluates; copied into its pooled
// output at the end, so an early return (no Final Pose) leaves no stale list.
const _plays = []
const _layerPlays = []   // the same, for Layered Bone Blend layer clips

function _syncObjectFromMap(obj, map) {
  // Drop stale keys, then write current entries. ~28 keys typical, so the
  // for-in scan is cheaper than reallocating a fresh object every frame.
  for (const k in obj) if (!map.has(k)) delete obj[k]
  for (const [k, v] of map) obj[k] = v
}

/**
 * Evaluate the full graph starting from the FinalPoseNode.
 *
 * Returns a pooled object — DO NOT cache the inner `weights` / `loopSettings`
 * / `additiveSettings` references across frames; they're mutated in place by
 * the next call. Iterate within the same frame and you're fine.
 *
 * @param {{ nodes: object[], edges: object[] }} graphState
 * @returns {{weights: Record<string, number> | null, loopSettings: Record<string, boolean>, additiveSettings: Record<string, boolean>, speedSettings: Record<string, number>, plays: object[]}}
 *   `plays` holds the frame's play requests ({ clipName, weight, loop, speed,
 *   additive, scope, nodeId }), one per Play Clip node reached; the request
 *   objects are long-lived and re-stamped each frame, so read them within the
 *   frame. `weights` / loop / additive / speed are the per-clip totals.
 */
const _lbbConfig = {
  blendBones: [], blendWeight: 1, mode: 'override', additiveRef: 'firstFrame',
  layer: [],  // the layer's play requests (see the layeredBoneBlend case)
}
const _lbbLegacyBones = new Map() // blendBone string → [blendBone], stable per value

/** The node's branch bones: `blendBones`, or the single `blendBone` of an older graph. */
export function lbbBones(data) {
  if (Array.isArray(data?.blendBones)) return data.blendBones
  const one = data?.blendBone ?? ''
  let arr = _lbbLegacyBones.get(one)
  if (!arr) { arr = one ? [one] : []; _lbbLegacyBones.set(one, arr) }
  return arr
}

export function evaluateGraph(graph) {
  const { nodes, edges } = graph
  _valueCache.clear()
  _pluginTicked.clear()
  _evalFrame++
  _plays.length = 0
  _layerPlays.length = 0

  const result      = new Map()
  const loopMap     = new Map()
  const additiveMap = new Map()
  const speedMap    = new Map()
  const ctx = buildCtx(nodes, edges, result, loopMap, additiveMap, speedMap)
  // Play requests are scoped to the GRAPH object (graphState / faceGraphState),
  // not to its node array, so an edit that replaces the nodes keeps the
  // players of the nodes it did not touch.
  ctx.scope = rootScopeFor(graph)
  ctx.plays = _plays
  // LBB accumulator maps — populated only when a layeredBoneBlend node is active.
  ctx.lbbResult      = new Map()
  ctx.lbbLoopMap     = new Map()
  ctx.lbbAdditiveMap = new Map()

  // Final Pose takes the pose on its `result` input and, in the face graph, a
  // Look node on its `look` input. Pick the pose edge by handle (an older graph
  // has no handle on it at all) — taking the first edge in would let a Look
  // wire stand in for the pose.
  const finalBucket = ctx.edgesByTarget.get('final')
  let finalEdge
  let lookEdge
  if (finalBucket) {
    for (const e of finalBucket) {
      if (e.targetHandle === 'look') { if (!lookEdge) lookEdge = e }
      else if (!finalEdge) finalEdge = e
    }
  }

  // Sideband: tick perFrame plugin nodes every frame regardless of graph
  // topology, and emit their declared sentinelClip into ctx.result so
  // downstream watchers react even when graph traversal doesn't reach the
  // node (e.g. its `anim` edge lost a tie to a sibling source at a shared
  // input pin).
  // This sideband is the SINGLE owner of perFrame tick + sentinel emission; the
  // inline branch in evaluateNode reuses the same pluginTickOnce guard and skips
  // re-emitting their sentinel, so a perFrame node ticks once and its sentinel
  // weight is counted once.
  for (const node of nodes) {
    if (!node.type?.startsWith('plugin.')) continue
    const entry = pluginRegistryGet(node.type)
    if (!entry?.nodeManifest?.perFrame || entry.nodeManifest.phase === 'postMixer' || !entry.instance?.tick) continue
    const w = pluginTickOnce(node, entry, ctx)
    const sentinel = entry.nodeManifest?.sentinelClip
    if (sentinel && w > 0) {
      ctx.result.set(sentinel, (ctx.result.get(sentinel) ?? 0) + w)
    }
  }

  // The Look node: not an animation to mix but adjustments applied after the
  // pose (head/eye aim, blinks), so it rides its own input and emits the same
  // sentinels the standalone Head Follow / Eye Follow / Blink nodes do.
  if (lookEdge) evaluateLook(lookEdge.source, ctx)

  if (!finalEdge && !lookEdge) return EMPTY_OUTPUT

  if (finalEdge) evaluateNode(finalEdge.source, ctx, 1.0, false)

  const out = _getOutput(nodes)
  if (out.weights === null) out.weights = {}
  _syncObjectFromMap(out.weights,          result)
  _syncObjectFromMap(out.loopSettings,     loopMap)
  _syncObjectFromMap(out.additiveSettings, additiveMap)
  _syncObjectFromMap(out.speedSettings,    speedMap)
  if (!out.plays) out.plays = []
  out.plays.length = 0
  for (let i = 0; i < _plays.length; i++) out.plays.push(_plays[i])
  if (!out.layerPlays) out.layerPlays = []
  out.layerPlays.length = 0
  for (let i = 0; i < _layerPlays.length; i++) out.layerPlays.push(_layerPlays[i])
  return out
}

/**
 * updateSMTransitions — evaluate transition rules for all SM nodes.
 * Must be called every frame BEFORE evaluateGraph so smRuntime is up to date.
 *
 * Transition rule types (stored in edge.data.condition):
 *   'manual'  — only fires when user clicks "go" (default)
 *   'time'    — auto-fires after edge.data.conditionValue seconds in source state
 *
 * @param {{ nodes: object[], edges: object[] }} graphState
 */
export function updateSMTransitions(graph) {
  const { nodes } = graph
  // Orphan sweep bookkeeping: every SM this call visits is stamped with this
  // graph and this call's tick, so the sweep below can tell "my SM that has
  // left the graph" from "an SM that belongs to the other graph" (body and
  // face are separate calls into the one smRuntime). Keyed by the graph
  // object, which is a module singleton per graph — no per-frame allocation.
  const tick = (_smSweepTick.get(graph) ?? 0) + 1
  _smSweepTick.set(graph, tick)

  for (const node of nodes) {
    if (node.type !== 'stateMachine') continue

    // Auto-init runtime (currentState = null until rules decide otherwise)
    if (!smRuntime[node.id]) {
      smRuntime[node.id] = { currentState: null, stateEnteredAt: null, transition: null }
    }
    const runtime = smRuntime[node.id]
    runtime._graph = graph
    runtime._tick  = tick

    const innerEdges = node.data.innerEdges ?? []
    const innerNodes = node.data.innerNodes ?? []

    // Track the entry edge target — reset currentState if it changes or is removed
    const firstEntry     = innerEdges.find(e => e.source === 'entry')
    const entryTarget    = firstEntry?.target ?? null
    const entryChanged   = runtime._entryTarget !== entryTarget
    if (entryChanged) {
      runtime._entryTarget = entryTarget
      // A silent drop still exits the state it leaves (smStateEvents.js).
      if (runtime.currentState) {
        exitState(runtime, node.id, findInnerNode(innerNodes, runtime.currentState), null, 'dropped')
      }
      runtime.currentState  = null
      runtime.pickedClip    = null
      runtime.transition    = null
    } else if (runtime.currentState && !findInnerNode(innerNodes, runtime.currentState)) {
      // The active state was deleted out from under the machine. Exit it from
      // the cached list, then re-enter through the Entry edge below.
      exitState(runtime, node.id, null, null, 'dropped')
      runtime.currentState  = null
      runtime.pickedClip    = null
      runtime.transition    = null
    }

    if (runtime.transition) continue  // mid-crossfade, wait

    if (!runtime.currentState) {
      // ── No active state: always enter via first Entry edge (no rule) ──
      if (firstEntry) {
        runtime.currentState   = firstEntry.target
        runtime.stateEnteredAt = Date.now()
        const entryStateNode   = findInnerNode(innerNodes, firstEntry.target)
        runtime.pickedClip     = pickClipForState(entryStateNode)
        // Re-roll any random nodes inside this state so each entry picks fresh
        clearStickyRandomsInNodes(entryStateNode?.data?.innerNodes)
        const clipInfo = runtime.pickedClip ? `clip: "${runtime.pickedClip}"` : '(subgraph / no clip)'
        devLog(`[SM] ${node.id} | init → "${firstEntry.target}" | ${clipInfo}`)
        enterState(runtime, node.id, entryStateNode, null)
      }
    } else {
      // Build context for anim-time rule nodes — find clip from state's inner graph
      const currentStateNode = findInnerNode(innerNodes, runtime.currentState)
      const sEdges = currentStateNode?.data?.innerEdges ?? []
      const sNodes = currentStateNode?.data?.innerNodes ?? []
      const outEdge = sEdges.find(e => e.target === 'output')
      const playNode = outEdge ? sNodes.find(n => n.id === outEdge.source && n.type === 'playClip') : null
      const currentClipName = playNode?.data?.clipName ?? null
      // Time Remaining / Ratio read THIS state's own player — not whichever
      // copy of the clip happens to be heaviest, which another state machine
      // on the same clip, or this state's own outgoing copy, could be.
      const clipTiming = stateClipTiming(runtime, node.id, playNode)
      // stateEnteredAt feeds the Time In State rule node (RuleEvaluator).
      const context = { currentClipName, clipTiming, stateEnteredAt: runtime.stateEnteredAt }

      // ── Check outgoing state-to-state transitions ──
      const outEdges = innerEdges.filter(
        e => e.source === runtime.currentState && e.source !== 'entry'
      )
      for (const edge of outEdges) {
        const ruleGraph = edge.data?.ruleGraph
        if (!ruleGraph?.nodes?.length) continue
        const result = evaluateRule(ruleGraph, context)
        debugState.ruleResults[`${node.id}::${edge.id}`] = result
        if (result) {
          const toStateNode = findInnerNode(innerNodes, edge.target)
          const fromState = runtime.currentState  // capture before triggerTransition flips it
          // Re-roll random nodes in the incoming state
          clearStickyRandomsInNodes(toStateNode?.data?.innerNodes)
          // Per-transition crossfade; a transition that authors none gets the
          // shared 0.3s this call site used to hardcode (resolveTransitionDuration).
          triggerTransition(node.id, runtime.currentState, edge.target,
            resolveTransitionDuration(edge.data), toStateNode)
          // All three fire NOW, at the start of the crossfade, in this order:
          // the old state's On Exit, the transition's Actions (ENG-1062), the
          // new state's On Enter. Decoupled seam — never imports EventRuntime
          // onto this hot path.
          exitState(runtime, node.id, currentStateNode, edge.target, 'transition')
          runTransitionActions(edge.data?.actions, { smNodeId: node.id, from: fromState, to: edge.target })
          enterState(runtime, node.id, toStateNode, fromState)
          break
        }
      }
    }

    // Store current active state for visual feedback
    debugState.smCurrentState[node.id] = runtime.currentState
  }

  // ── Orphan sweep: an SM of THIS graph that the loop above did not visit has
  // left the graph (deleted, or the blueprint / character was replaced). Its
  // active state is exited — the silent-drop rule — and its runtime dropped,
  // so a later graph that reuses the id starts from Entry instead of
  // inheriting a state from a different machine.
  for (const id in smRuntime) {
    const runtime = smRuntime[id]
    if (runtime._graph !== graph || runtime._tick === tick) continue
    if (runtime.currentState) exitState(runtime, id, null, null, 'dropped')
    delete smRuntime[id]
    delete debugState.smCurrentState[id]
  }
}

// Per-graph call counter for the orphan sweep in updateSMTransitions.
const _smSweepTick = new Map()

// Allocation-free lookup of a state machine's inner node by id.
function findInnerNode(innerNodes, id) {
  for (let i = 0; i < innerNodes.length; i++) {
    if (innerNodes[i].id === id) return innerNodes[i]
  }
  return null
}

/**
 * Place the avatar group from an extracted clip's own root motion — where the
 * character is and which way it faces, for the frame the mixer has JUST applied.
 *
 * MUST run AFTER mixer.update(), and that is the whole point of it being its own
 * function. It reads `action.time`, and the mixer is what advances that: called
 * from applyWeights (which runs before the update) it placed the group using the
 * PREVIOUS frame's time while the pose on screen was a frame ahead. The gap is
 * v * delta — about 2 cm at 60 fps and a walking pace, but 6-7 cm on a 50 ms
 * hitch — and since it varies with the frame time rather than staying constant,
 * it reads as the walk jittering whenever the frame rate is uneven. Reading the
 * time after the advance also picks up the followAction delta clamp for free.
 *
 * Scalar work, no allocation. A no-op unless a direct-played clip carries a
 * rootDrive, so gestures and preset clips never enter it.
 *
 * @param {object} actions  window.__AVATAR_ACTIONS__
 */
export function applyRootDrive(actions) {
  const w = typeof window !== 'undefined' ? window : null
  const dp = w?.__DIRECT_PLAY__
  const rd = dp?.rootDrive
  if (!rd) return
  const a = actions?.[dp.clipName]
  const g = w.__AVATAR_GROUP__
  if (!a || !g) return

  const fPos = Math.min(rd.frames - 1, Math.max(0, a.time * rd.fps))
  const i0 = Math.floor(fPos)
  const i1 = Math.min(rd.frames - 1, i0 + 1)
  const ft = fPos - i0
  const dx = rd.dx[i0] + (rd.dx[i1] - rd.dx[i0]) * ft
  const dz = rd.dz[i0] + (rd.dz[i1] - rd.dz[i0]) * ft
  const dy = rd.dyaw[i0] + (rd.dyaw[i1] - rd.dyaw[i0]) * ft
  // The delta is in the clip's own frame; the group may already be turned, and
  // the clip plays INSIDE it, so the travel rotates with it. (THREE's Y
  // rotation: x' = x cos + z sin, z' = -x sin + z cos.)
  const c = Math.cos(rd.baseYaw), sn = Math.sin(rd.baseYaw)
  g.position.x = rd.baseX + dx * c + dz * sn
  g.position.z = rd.baseZ - dx * sn + dz * c
  g.rotation.y = rd.baseYaw + dy
}

// applyWeights' frame counter. A request applied on the previous call is
// "continuing"; anything else starts fresh (ENG-435). A non-looping clip that
// finished sits paused at time === duration with clampWhenFinished holding its
// final pose, and so does one RE-driven after a gap; time alone can't tell them
// apart, so the request remembers the call that last applied it. Skipping a
// number — the direct-play early return does — makes every request fresh on
// the next call.
let _applyFrame = 0
// Requests that currently hold a player, swept each call: a request not applied
// this call (weight 0, or no longer reached) returns its player to the pool.
const _liveRequests = []

/** The live graph players (debug / tests): `{ clipName, scope, nodeId, weight, action }`. */
export function livePlayers() {
  return _liveRequests.map(r => ({
    clipName: r.clipName, scope: r.scope.label, nodeId: r.nodeId,
    weight: r.player?.action.getEffectiveWeight() ?? 0, action: r.player?.action ?? null,
  }))
}

/**
 * The live graph play requests, each with its `player` — the array itself,
 * not a copy (read it within the frame; applyWeights compacts it in place).
 * The Layered Bone Blend runtime silences and restores these around its pass.
 */
export function liveGraphRequests() {
  return _liveRequests
}

/** The actions of the live graph players on `clipName`, heaviest first. */
export function playersForClip(clipName) {
  const out = []
  for (const r of _liveRequests) if (r.clipName === clipName && r.player) out.push(r.player.action)
  return out.sort((a, b) => b.getEffectiveWeight() - a.getEffectiveWeight())
}

// The graph's players, for tests and the console. __AVATAR_ACTIONS__ is the
// per-clip TEMPLATE map (what direct play, gestures and the layer runtime
// drive); what the GRAPH is playing lives here, one entry per Play Clip node.
if (typeof window !== 'undefined') {
  window.__ANIM_PLAYERS__ = {
    live: livePlayers,
    forClip: playersForClip,
    /** Pool counts for the mixer `clipName`'s template belongs to. */
    stats: () => {
      const m = Object.values(window.__AVATAR_ACTIONS__ ?? {})[0]?.getMixer?.()
      return m ? playerPoolFor(m).stats() : null
    },
  }
}

function releaseRequest(r) {
  if (r.player) r.player.pool.release(r.player)
  r.player = null
  r.live = false
}

/**
 * Drive the mixer from the graphs' play requests (evaluateGraph().plays), one
 * player per request (animPlayerPool.js).
 *
 * The clip TEMPLATES in `actions` (window.__AVATAR_ACTIONS__, one per clip
 * name) are no longer driven by the graph; they belong to the things that
 * play a clip outside it — __DIRECT_PLAY__, the gesture channel, the Layered
 * Bone Blend layer. Every template that none of those holds is stopped here
 * each frame, as before.
 *
 * @param {Array<Array<object>|null>} playLists  play requests, one list per
 *   graph (body, then the face graph's clips that live in this mixer). A
 *   request whose clip is not registered is pulled forward through the clip
 *   loader (JIT) and skipped this frame.
 * @param {Record<string, THREE.AnimationAction>} actions  clip templates
 * @param {Record<string, THREE.AnimationAction>} [additiveActions]  pre-baked
 *   additive clones of non-additive clips, by clip name
 * @param {Set<string>} [additiveClipNames]  clips authored additive (their
 *   template IS additive; no clone)
 */
export function applyWeights(playLists, actions, additiveActions, additiveClipNames) {
  // Direct-play override: EventRuntime can set window.__DIRECT_PLAY__ to take exclusive control of one clip
  const w = typeof window !== 'undefined' ? window : null
  const dp = w?.__DIRECT_PLAY__
  // Set while a direct-play clip is fading out and the graph runs alongside it:
  // the fading action owns its own weight, so the zero-out below must leave it
  // alone.
  let tailClip = null
  // What the graph's weights are multiplied by this frame: 1, or the
  // complement of a direct-play clip's fade-out (the tail blend below).
  let playScale = 1
  if (dp) {
    // A NEW direct play starts its clip from frame 0 — also when that clip is
    // already playing or holding its last frame from an earlier one (the
    // template is direct play's own player now that the graph has its own).
    // Before, a caller that did not reset() itself got the clip mid-way, and a
    // followAction hold on a finished clip expired on its first frame. Once
    // per direct-play object and clip; a clip still streaming in is reset when
    // it lands.
    if (dp._startedClip !== dp.clipName) {
      const a0 = actions[dp.clipName]
      if (a0) {
        dp._startedClip = dp.clipName
        a0.reset()
      }
    }
    // Expiry. Default: wall clock (dp.expiresAt). With dp.followAction the hold
    // instead tracks the ACTION's progress — under a stalled/clamped frame loop
    // (e.g. the GPU busy right after kimodo generation) the clip runs slower
    // than wall time, and a wall-clock expiry would cut it mid-motion. The clip
    // is done when the LoopOnce action has reached its end; expiresAt + 15s
    // stays as a wedge guard so a never-advancing action can't hold the graph
    // hostage forever.
    let expired
    const actFA = dp.followAction ? actions[dp.clipName] : null
    const clipDurFA = actFA?.getClip?.().duration
    if (actFA && clipDurFA) {
      expired = actFA.time >= clipDurFA - 1e-4 || Date.now() > dp.expiresAt + 15000
    } else {
      expired = Date.now() > dp.expiresAt
    }
    if (expired) {
      w.__DIRECT_PLAY__ = null
      // Release hook (e.g. generated locomotion commits its root travel to the
      // avatar group). Cleared first so a hook that plays another clip can set
      // a fresh __DIRECT_PLAY__ without this frame nulling it.
      if (typeof dp.onExpire === 'function') {
        try { dp.onExpire() } catch (err) { console.warn('[applyWeights] DIRECT_PLAY onExpire threw:', err) }
      }
    } else {
      // One-shot log when a new __DIRECT_PLAY__ kicks in — confirms the render
      // loop is actually picking up the clip the Test button asked for, and
      // shows the action it resolved to.
      if (dp._loggedClipName !== dp.clipName) {
        dp._loggedClipName = dp.clipName
        const a0 = actions[dp.clipName]
        const c0 = a0?.getClip?.()
        console.log(
          `[applyWeights] DIRECT_PLAY engaged for "${dp.clipName}" — keepBase=${!!dp.keepBase}`,
          `\n  action: ${a0 ? 'found' : 'MISSING'}`,
          a0 ? `blendMode=${a0.blendMode} (${a0.blendMode === AdditiveAnimationBlendMode ? 'ADDITIVE' : 'NORMAL'}), tracks=${c0?.tracks?.length}, duration=${c0?.duration?.toFixed(3)}s` : '',
          `\n  other actions running (will be ${dp.keepBase ? 'KEPT' : 'STOPPED'}):`,
          Object.entries(actions).filter(([n, a]) => n !== dp.clipName && a.isRunning()).map(([n]) => n).join(', ') || '(none)'
        )
      }
      // keepBase: true — let the SM continue driving its base clip so that
      // near-static overlay clips (e.g. thinking_01 with only a few active
      // tracks) blend on top of the current pose instead of snapping to A-pose.
      if (!dp.keepBase) {
        // CROSSFADE, not a cut. Stopping the outgoing actions on the first
        // direct-play frame leaves the incoming clip — still at weight ~0 from
        // its own blend-in — as the only thing driving the skeleton, and THREE
        // blends a sub-weight action toward its ORIGINAL (bind) value. So the
        // pose snapped to the A-pose and ramped in from there, which reads as a
        // skeleton jump at the start of every generated clip. Fading the
        // outgoing weight down over the same window makes the two complementary
        // (they sum to 1) and the mixer normalises them into a true crossfade.
        // The keepBase branch is already documented as avoiding exactly this
        // snap; this gives the non-keepBase path the same property without
        // leaving a second FULL-weight action driving pelvis.position, which is
        // what the duplicate-action sweep below blames for a half-length walk.
        const fadeMs = dp.blendInMs | 0
        const outWeight = fadeMs > 0 && dp.startTime != null
          ? 1 - (Date.now() - dp.startTime) / fadeMs
          : 0
        for (const [clipName, action] of Object.entries(actions)) {
          if (clipName === dp.clipName || clipName === dp.baseClipName) continue
          if (!action.isRunning()) continue
          if (outWeight > 0) action.setEffectiveWeight(outWeight)
          else action.stop()
        }
        // The registry (`actions`) has ONE action per clip name, but the mixer
        // itself can hold additional normal-blend actions bound to a different
        // clip object with the SAME name (observed empirically). A duplicate
        // left running at weight 1 makes the mixer AVERAGE it with the
        // direct-played clip on every shared track — e.g. a generated walk
        // travels exactly half its root distance. Direct-play is exclusive
        // control, so sweep the mixer itself rather than just the registry.
        // TODO: the exact source of the duplicate normal-blend action is not yet
        // confirmed (it is NOT the additive clones below, which this loop skips);
        // once found, prevent the duplicate registration and drop this sweep.
        const dpAction = actions[dp.clipName]
        const mixerActions = dpAction?.getMixer?.()._actions
        if (mixerActions) {
          // A copy: stop() deactivates the action, which reorders _actions in
          // place, and iterating the live array skips the action moved into
          // the stopped one's slot (two graph players on one clip: the second
          // kept playing under the direct clip).
          for (const other of mixerActions.slice()) {
            if (other === dpAction) continue
            // Leave ADDITIVE actions alone: they blend on top (not averaged
            // like normal duplicates), the additive block below already
            // weight-zeroes them, and stop()ing them here resets their phase
            // so a persistent overlay (e.g. breathing) hitches/restarts when
            // the direct-play clip ends. Only sweep normal-blend duplicates.
            if (other.blendMode === AdditiveAnimationBlendMode) continue
            // Only the TEMPLATES of the direct clip and its base are exempt.
            // The graph's own players carry the same clip name (animPlayerPool
            // views), and one left at full weight is averaged with the direct
            // clip: Direct Play of the clip the graph is playing showed half
            // of each pose.
            const n = other.getClip().name
            if ((n === dp.clipName || n === dp.baseClipName) && other === actions[n]) continue
            // Same fade as above: a duplicate cut dead is as visible as a base
            // clip cut dead, and it is often the one actually driving the pose.
            if (outWeight > 0) { other.setEffectiveWeight(outWeight); continue }
            if (other.isRunning()) other.stop()
            other.setEffectiveWeight(0)
          }
        }
      }
      if (additiveActions) {
        for (const [clipName, action] of Object.entries(additiveActions)) {
          if (clipName === dp.baseClipName) continue
          action.setEffectiveWeight(0)
        }
      }
      // The graph's own additive players, likewise (the sweep above leaves
      // additive actions alone so a breathing overlay keeps its phase).
      for (let i = 0; i < _liveRequests.length; i++) {
        const a = _liveRequests[i].player?.action
        if (a && a.blendMode === AdditiveAnimationBlendMode) a.setEffectiveWeight(0)
      }
      // And the gesture channel's players (additive, so the sweep skips them).
      silenceGestures()
      // Additive preview reconstruction: when dp.baseClipName is set, drive that
      // clip as a normal base pose so an additive dp.clipName shows as
      // base + delta (the full gesture) instead of the raw delta on the A-pose.
      if (dp.baseClipName && dp.baseClipName !== dp.clipName) {
        const base = actions[dp.baseClipName]
        if (base) {
          base.setLoop(2201 /* THREE.LoopRepeat */, Infinity)
          base.setEffectiveWeight(1)
          if (!base.isRunning()) base.play()
        }
      }
      const a = actions[dp.clipName]
      if (!a) {
        // JIT: a direct-played clip may still be streaming in — pull it forward.
        getBodyClipLoader()?.request(dp.clipName)
      }
      // Hoisted out of the block below so the tail blend can read them: the
      // envelope decides whether the graph runs scaled this frame.
      let dpWeight = 1
      let dpBlendOut = 0
      if (a) {
        // Linear blend-in / blend-out envelope. When dp.blendInMs and
        // dp.blendOutMs are absent or 0, this collapses to a flat 1.0
        // (legacy behavior). All ops are scalar — no per-frame allocations.
        let weight = 1
        const blendInMs  = dp.blendInMs  | 0
        const blendOutMs = dp.blendOutMs | 0
        const now = Date.now()
        if (blendInMs > 0 && dp.startTime != null) {
          const inRatio = (now - dp.startTime) / blendInMs
          if (inRatio < weight) weight = inRatio < 0 ? 0 : inRatio
        }
        if (blendOutMs > 0) {
          // followAction: fade over the clip's LAST blendOutMs of clip-time, so
          // the tail blend is frame-rate independent (see expiry note above).
          const outRatio = dp.followAction && a.getClip?.().duration
            ? ((a.getClip().duration - a.time) * 1000) / blendOutMs
            : (dp.expiresAt - now) / blendOutMs
          if (outRatio < weight) weight = outRatio < 0 ? 0 : outRatio
        }
        dpWeight = weight
        dpBlendOut = blendOutMs
        a.setEffectiveWeight(weight)
        if (!a.isRunning()) a.play()
      }
      // TAIL BLEND. While the direct clip fades out, let the normal pass below
      // run with the graph's weights scaled by the complement (playScale), so
      // the clip crossfades into whatever the state machine is driving rather
      // than toward the bind pose. Symmetric with the fade-IN above.
      //
      // Only safe for a clip whose root motion was EXTRACTED: one that still
      // carries travel in pelvis.position would be dragged back the whole
      // distance as its weight falls, which is what blendOutMs=0 exists to
      // prevent on that path.
      if (dp.rootDrive && dpBlendOut > 0 && dpWeight > 0 && dpWeight < 1) {
        playScale = 1 - dpWeight
        // The direct-played clip is a TEMPLATE, never one of the graph's
        // players, so the graph pass cannot re-play it or re-apply loop
        // settings to it in its final frames. It is excluded from the
        // template zero-out instead.
        tailClip = dp.clipName
        // Fall through to the normal pass rather than returning.
      } else {

      // DIRECT_PLAY stops/overrides the normal actions; once it ends, every
      // graph-driven clip should restart cleanly rather than resume a stale,
      // possibly-finished action. Skipping a frame number makes every request
      // fresh on the next call (ENG-435).
      _applyFrame++
      debugState.tick++
      return
      }
    }
  }

  _applyFrame++

  // Stop every clip TEMPLATE that a fading direct play doesn't hold. A stopped
  // action is skipped by mixer.update entirely — keeping every registered clip
  // "running at weight 0" would force the mixer to interpolate every track of
  // every clip every frame. The graph, the Layered Bone Blend layer and the
  // gesture channel all play on players of their own (animPlayerPool.js), so
  // outside direct play nothing plays a template.
  for (const clipName in actions) {
    if (clipName === tailClip) continue
    const action = actions[clipName]
    if (action.isRunning()) action.stop()
    else action.setEffectiveWeight(0)
  }
  // Additive clones are templates too. Manifest-additive clips share their
  // single in-place-baked action with `actions` (no entry here).
  if (additiveActions) {
    for (const clipName in additiveActions) additiveActions[clipName].setEffectiveWeight(0)
  }

  // One player per request. for-index loops over the lists — no iterator
  // objects on the hot path. blendMode / setLoop / clampWhenFinished /
  // timeScale are only written when they'd actually change, so the
  // steady-state cost per active request is setEffectiveWeight + maybe play().
  if (playLists) {
    for (let li = 0; li < playLists.length; li++) {
      const list = playLists[li]
      if (!list) continue
      for (let i = 0; i < list.length; i++) {
        const r = list[i]
        const weight = r.weight * playScale
        if (weight <= 0 || r.appliedFrame === _applyFrame) continue
        const clipName = r.clipName
        const template = actions[clipName]
        if (!template) {
          // JIT: the graph is asking for a clip that hasn't registered yet. Most
          // body clips stream in the background after __AVATAR_READY__ — pull this
          // one to the front of the load queue so it's available within a tick or
          // two. request() is idempotent and cheap (Map.has + return in steady
          // state); it fires at most once per clip before that clip starts loading.
          const loader = getBodyClipLoader()
          if (loader) loader.request(clipName)
          // Only warn for a clip that's genuinely unknown to the loader — one it
          // doesn't own in any state (queued / in flight / loaded). A clip we just
          // pulled is being handled and will arrive shortly, so stay quiet.
          if (w?.__AVATAR_READY__ && !_missingClipWarned.has(clipName)
              && !(loader && loader.isManaged(clipName))) {
            _missingClipWarned.add(clipName)
            console.warn(`[applyWeights] Clip not in mixer: "${clipName}" (weight=${weight.toFixed(3)})`)
          }
          continue
        }
        // A Layered Bone Blend layer clip plays its plain template clip (the
        // layer runtime does its own additive maths); additive only if authored so.
        const isAdditive = (!r.layer && r.additive === true) || additiveClipNames?.has(clipName)
        // When additive, play the pre-baked additive clone. The clone is built
        // lazily (AvatarModel no longer bakes one for every clip up front —
        // that janked the load loop), so create it on first additive use here.
        let source = template
        if (isAdditive && !r.layer) {
          let clone = additiveActions?.[clipName]
          if (!clone && w?.__ENSURE_ADDITIVE_CLONE__) clone = w.__ENSURE_ADDITIVE_CLONE__(clipName)
          if (clone) source = clone
        }
        const sourceClip = source.getClip()

        // Continuing = applied on the previous call. Anything else is a fresh
        // (re)start: a new node, a re-entered state, the first frame after a
        // gap or after a direct-play hold.
        let fresh = r.appliedFrame !== _applyFrame - 1
        r.appliedFrame = _applyFrame
        let p = r.player
        if (p && p.source !== sourceClip) {
          // The node now names another clip (an edit, a prefab change, the
          // clip re-registered, additive toggled): a new player, from frame 0.
          releaseRequest(r)
          p = null
        }
        if (!p) {
          p = r.player = playerPoolFor(template.getMixer()).acquire(sourceClip)
          fresh = true
          if (!r.live) { r.live = true; _liveRequests.push(r) }
        }

        const a = p.action
        const shouldLoop = r.loop !== false
        const wantBlend  = isAdditive ? 2501 /* THREE.AdditiveAnimationBlendMode */ : 2500 /* THREE.NormalAnimationBlendMode */
        const wantLoop   = shouldLoop ? 2201 /* THREE.LoopRepeat */ : 2200 /* THREE.LoopOnce */
        if (a.blendMode !== wantBlend) a.blendMode = wantBlend
        if (a.loop !== wantLoop) a.setLoop(wantLoop, shouldLoop ? Infinity : 1)
        const wantClamp  = !shouldLoop
        if (a.clampWhenFinished !== wantClamp) a.clampWhenFinished = wantClamp
        const wantSpeed  = r.speed ?? 1
        if (a.timeScale !== wantSpeed) a.timeScale = wantSpeed
        // A layer clip plays silently: its time advances with the mixer, and
        // the layer runtime lends it weight only for its own pass.
        a.setEffectiveWeight(r.layer ? 0 : weight)
        if (!a.isRunning()) {
          // A non-running player is one of: just acquired (stopped), stopped by
          // a direct-play hold, or a finished LoopOnce clip paused at its final
          // frame (clampWhenFinished). Bare play() does NOT rewind a finished
          // LoopOnce action — it resumes paused at the end, leaving the clip
          // frozen forever (ENG-435). Rewind via reset() when the clip should
          // loop (a looping clip must never sit paused) OR on a fresh (re)start.
          // A non-looping clip driven continuously is left clamped so it holds
          // its final pose.
          if (shouldLoop || fresh) a.reset()
          a.play()
        }
      }
    }
  }

  // Return the players of every request not applied this call to the pool.
  // Compacted in place — no allocation.
  let keep = 0
  for (let i = 0; i < _liveRequests.length; i++) {
    const r = _liveRequests[i]
    if (r.appliedFrame === _applyFrame) _liveRequests[keep++] = r
    else releaseRequest(r)
  }
  _liveRequests.length = keep

  // Layer in any active body gesture AFTER the main weight loop so the
  // envelope-driven weight isn't clobbered by the graph's per-frame reset.
  applyGesture(actions)

  debugState.tick++
}

/**
 * Post-mixer deformer pass. Called from AvatarModel.useFrame AFTER mixer.update()
 * (and skeleton update), so plugin deformer nodes (graph body/face,
 * phase:"postMixer") see the FINAL skinned pose — which a rig-driven ONNX cloth/
 * face deformer needs, vs the pre-mixer weight pass that only produces clip weights.
 *
 * Each such node's `deform(frameCtx, data, id)` runs once per frame. `frameCtx`
 * exposes the posed rig + audio state: `{ delta, getSkeleton(), getArkit() }`.
 * Deformers drive their own meshes (GPU morph / vertex upload) and contribute no
 * clip weights, so nothing is returned. Allocation-free: no ctx/Maps built here;
 * deformers read posed state from frameCtx (wired postMixer pin inputs are a later
 * addition). One bad deformer never stalls the loop (errors swallowed).
 */
export function evaluatePostMixer({ nodes }, frameCtx) {
  for (const node of nodes) {
    if (!node.type?.startsWith('plugin.')) continue
    const entry = pluginRegistryGet(node.type)
    if (entry?.nodeManifest?.phase !== 'postMixer' || !entry.instance?.deform) continue
    if ((node.data?.enabled ?? true) === false) continue
    try { entry.instance.deform(frameCtx, node.data, node.id) }
    catch (e) { console.error(`[GraphEvaluator] plugin deform failed for ${node.type}:`, e) }
  }
}

// Test/debug hooks: evaluate a hand-built graph / run the post-mixer deform pass
// directly (used by tests/e2e/ci/plugin-system.spec.js to exercise plugin pin
// dataflow, the single-tick guarantee, and the deformer lane off the render loop).
if (typeof window !== 'undefined') {
  window.__EVALUATE_GRAPH__ = evaluateGraph
  window.__EVALUATE_POSTMIXER__ = evaluatePostMixer
}
